/**
 * Reveal a section of the page the user is already on — scroll it into view
 * (instantly under reduced motion) and move focus to its heading, so keyboard
 * and screen-reader users land there too. Returns false when no element with
 * that id is on the page (the caller then navigates instead).
 *
 * Used by the Needs-you list for a same-page target such as `/#competitors`
 * (the Dashboard's own Tracked competitors section).
 */
export function revealOnPage(id: string): boolean {
  const target = document.getElementById(id);
  if (!target) return false;
  // A target inside a closed <details> (the Dashboard's reference fold,
  // ENG-APP-UX-TUNEUP C9 W3-A) is revealed by opening every closed ancestor
  // first: scrollIntoView would scroll to nothing and focus() would fail.
  let closed = target.parentElement?.closest("details:not([open])") ?? null;
  while (closed) {
    (closed as HTMLDetailsElement).open = true;
    closed = closed.parentElement?.closest("details:not([open])") ?? null;
  }
  const reduceMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  target.scrollIntoView({
    behavior: reduceMotion ? "auto" : "smooth",
    block: "start",
  });
  const heading = target.querySelector<HTMLElement>("h2, h3");
  if (heading) {
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
  }
  return true;
}

/**
 * Split an app href into (path, hash): `/#competitors` → ["/", "competitors"],
 * `/experiments/active` → ["/experiments/active", null].
 */
export function splitHref(href: string): [string, string | null] {
  const at = href.indexOf("#");
  if (at === -1) return [href, null];
  return [href.slice(0, at) || "/", href.slice(at + 1) || null];
}
